/**
 * Versioned-document sync (SPEC §1A.3). Pure merge logic lives here so it can be unit-tested
 * without a browser; the network layer (fetch + If-Match) is in client.ts.
 */

export type DocType =
  | "settings"
  | "layout"
  | "drawings"
  | "watchlist"
  | "bracket_presets"
  | "hotkeys"
  | "alerts_meta"
  | "risk_settings"
  /** Owner-confirmed R|Trader Pro match per account (ADR-0045, tables/brokerRisk.ts). */
  | "broker_verification"
  /**
   * GEX levels per futures root (`gex_levels/NQ`), written by the GEX feeder and only read by
   * clients (pine/bundled.ts, MTA GEX Levels); crates/api docs.rs + gex.rs (ADR-0048).
   */
  | "gex_levels"
  /** Owner-entered firm rules per account (ADR-0047, tables/firmRules.ts). */
  | "firm_rules";

/** Field-mergeable types merge per top-level key; whole-document types keep the server copy. */
export const MERGE_STRATEGY: Record<DocType, "field" | "whole" | "server_authoritative"> = {
  settings: "field",
  bracket_presets: "field",
  hotkeys: "field",
  watchlist: "field",
  alerts_meta: "field",
  layout: "whole",
  drawings: "whole",
  risk_settings: "server_authoritative",
  // Another device's confirmation (or withdrawal) wins; this one must look again.
  broker_verification: "server_authoritative",
  // Read-only for clients: the feeder's copy is the only one.
  gex_levels: "server_authoritative",
  // The owner reviews and saves the whole rule set; another device's save wins, look again.
  firm_rules: "server_authoritative",
};

export type Doc<T = Record<string, unknown>> = {
  docType: DocType;
  docId: string;
  version: number;
  body: T;
};

export type ConflictResolution<T> =
  { kind: "retry"; body: T } | { kind: "keep_server"; server: T; local: T } | { kind: "rejected"; server: T };

/**
 * Resolve a 409 per SPEC §1A.3 rule 3.
 * @param base   the version the client started editing from
 * @param local  the client's edited body
 * @param server the current server body returned with the 409
 */
export function resolveConflict<T extends Record<string, unknown>>(
  docType: DocType,
  base: T,
  local: T,
  server: T,
): ConflictResolution<T> {
  switch (MERGE_STRATEGY[docType]) {
    case "server_authoritative":
      return { kind: "rejected", server };
    case "whole":
      return { kind: "keep_server", server, local };
    case "field": {
      // Local wins for keys the user edited (differ from base); server wins otherwise.
      const merged: Record<string, unknown> = { ...server };
      for (const key of new Set([...Object.keys(base), ...Object.keys(local)])) {
        if (!deepEqual(base[key], local[key])) merged[key] = local[key];
      }
      return { kind: "retry", body: merged as T };
    }
  }
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (!isRecord(a) || !isRecord(b)) return false;
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  return ka.every((k) => deepEqual(a[k], b[k]));
}
